/**
 * AVISOS DE LA APLICACIÓN: CARNÉ Y MÁQUINA (F4 del plan del cuaderno,
 * 26-09-2026; D2: H6 y H7, ahora que existen F2 y F3).
 *
 *  - H6 · el carné del aplicador está CADUCADO el día de la aplicación, o
 *    caduca en menos de 60 días; o la persona no tiene carné dado de alta.
 *  - H7 · la máquina va al ROMA (o al censo) y no tiene el nº; o su inspección
 *    está VENCIDA ese día, o vence en menos de 60 días; o está sujeta a
 *    inspección y no tiene fechas. Una mochila o carretilla de menos de 100 L
 *    está EXENTA: ni nº ni inspección, ni avisos falsos. Un tipo «otro» no se
 *    sabe qué es y no avisa de nada (como en `equipmentRules`).
 *
 * NINGUNO BLOQUEA (T4): la tarea se crea y el parte se guarda igual; esto se
 * enseña para que alguien lo arregle antes de que llegue al cuaderno.
 *
 * SIN DATO NO ES «OK» (A15): una persona sin carné registrado o una máquina
 * sujeta sin fechas dan `license_no_data` / `inspection_no_data` («sin dato»),
 * nunca silencio que se lea como «en regla». Lo que SÍ no avisa es no saber
 * QUIÉN o CON QUÉ (sin aplicador ni máquina): eso es un hueco del cuaderno, que
 * el cuaderno cuenta, no un aviso de este producto ni de esta tarea.
 *
 * Las reglas son las de F2 y F3 (`equipmentRules`, `applicatorRules`, cada una
 * con su gemela del servidor y su paridad): aquí no se recalcula nada, solo se
 * traduce a avisos. Fechas 'AAAA-MM-DD', la de la APLICACIÓN (la programada al
 * crear la tarea; la del parte en el registro manual).
 */
import {
  equipmentLabel,
  equipmentRegime,
  inspectionStatus,
  nextInspectionOn,
  daysBetween,
  type EquipmentRow,
} from "@/lib/machinery/equipmentRules";
import { licenseExpiresOn, licenseStatus, daysBetweenIso, type ApplicatorRow } from "@/lib/applicators/applicatorRules";

export type ApplicationCheck =
  | { kind: "license_expired"; who: string | null; expiresOn: string }
  | { kind: "license_soon"; who: string | null; expiresOn: string; daysLeft: number }
  | { kind: "license_no_data"; who: string | null }
  | { kind: "equipment_no_roma"; label: string }
  | { kind: "equipment_no_census"; label: string }
  | { kind: "inspection_overdue"; label: string; nextOn: string }
  | { kind: "inspection_soon"; label: string; nextOn: string; daysLeft: number }
  | { kind: "inspection_no_data"; label: string };

export interface ApplicationChecksInput {
  /**
   * QUIÉN aplica. `null`/ausente = no se sabe todavía (nadie asignado): no se
   * avisa. Con persona y sin fila de carné (`row: null`) = «sin dato».
   */
  applicator?: { row: ApplicatorRow | null; personName?: string | null } | null;
  /** CON QUÉ. `null`/ausente = sin máquina: no se avisa (es un hueco del cuaderno). */
  equipment?: EquipmentRow | null;
  /** 'AAAA-MM-DD' de la aplicación. */
  date: string;
}

const clean = (s: string | null | undefined) => {
  const v = (s ?? "").trim();
  return v ? v : null;
};

function licenseChecks(a: NonNullable<ApplicationChecksInput["applicator"]>, date: string): ApplicationCheck[] {
  const row = a.row;
  const who = clean(row?.display_name) ?? clean(a.personName);
  if (!row) return [{ kind: "license_no_data", who }];
  const expiresOn = licenseExpiresOn(row.license_issued_on, row.license_expires_on);
  const status = licenseStatus(expiresOn, date);
  if (status === "expired") return [{ kind: "license_expired", who, expiresOn: expiresOn as string }];
  if (status === "soon") {
    return [{ kind: "license_soon", who, expiresOn: expiresOn as string, daysLeft: daysBetweenIso(date, expiresOn as string) ?? 0 }];
  }
  if (status === "unknown") return [{ kind: "license_no_data", who }];
  return [];
}

function equipmentChecks(e: EquipmentRow, date: string): ApplicationCheck[] {
  const regime = equipmentRegime(e.equipment_type);
  if (regime !== "roma" && regime !== "censo") return []; // exenta u «otro»: nada que avisar
  const label = equipmentLabel(e);
  const out: ApplicationCheck[] = [];
  if (regime === "roma" && !clean(e.roma_number)) out.push({ kind: "equipment_no_roma", label });
  if (regime === "censo" && !clean(e.census_reference)) out.push({ kind: "equipment_no_census", label });
  const status = inspectionStatus(e, date);
  const nextOn = nextInspectionOn(e);
  if (status === "overdue" && nextOn) out.push({ kind: "inspection_overdue", label, nextOn });
  else if (status === "soon" && nextOn) {
    out.push({ kind: "inspection_soon", label, nextOn, daysLeft: daysBetween(date, nextOn) ?? 0 });
  } else if (status === "unknown") out.push({ kind: "inspection_no_data", label });
  return out;
}

/** Los avisos de carné (H6) y de máquina (H7), en ese orden. Vacío = nada que decir. */
export function applicationChecks(input: ApplicationChecksInput): ApplicationCheck[] {
  const out: ApplicationCheck[] = [];
  if (input.applicator) out.push(...licenseChecks(input.applicator, input.date));
  if (input.equipment) out.push(...equipmentChecks(input.equipment, input.date));
  return out;
}

/** Los que son de verdad un problema (caducado, vencido, sin nº), no un «pronto» ni un «sin dato». */
export function isSeriousCheck(c: ApplicationCheck): boolean {
  return (
    c.kind === "license_expired" ||
    c.kind === "inspection_overdue" ||
    c.kind === "equipment_no_roma" ||
    c.kind === "equipment_no_census"
  );
}
